import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import {
  CreateRoomParams,
  LoginInMeeting,
  LoginInMeetingResponse,
  MeetingRoom,
  CreateEditMeetingRoom,
  RoomConnectParams,
  SendMessage,
  GetMessages,
  GetMessagesParams,
  DocProps,
  MeetingSettingsApi,
  RegisterParams,
  CurrentUser,
  MeetingHistoryItem
} from '@/types/api.ts';
import { showToast } from '@/components/ToastManager';

class Api {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        // The anonymous "am I logged in?" probe 401s on every page load for
        // logged-out visitors — don't surface that as an error toast.
        const isAuthProbe401 =
          error.response?.status === 401 && error.config?.url?.includes('/auth/users/me');
        const isLoginError =
          error.config?.url?.includes('/auth/jwt/login');
        if (!isAuthProbe401 && !isLoginError) {
          showToast('errorToast', 'error', 'Ошибка запроса', error.message);
        }
        return Promise.reject(error);
      }
    );
  }

  // ── Authentication (fastapi-users, mounted at /api/v1/auth) ────────────────

  public async register({ email, password, role }: RegisterParams): Promise<CurrentUser> {
    try {
      const { data } = await this.axiosInstance.post('/auth/register', {
        email,
        password,
        role
      });
      return data;
    } catch (error) {
      console.error('Error registering user:', error);
      throw new Error('Error registering user');
    }
  }

  public async login(email: string, password: string): Promise<void> {
    try {
      // fastapi-users /auth/jwt/login expects an OAuth2 password-flow form body
      // (`username` + `password`), not JSON. The JWT comes back in an HttpOnly
      // `auth_token` cookie, so there is nothing to return.
      const body = new URLSearchParams();
      body.append('username', email);
      body.append('password', password);
      await this.axiosInstance.post('/auth/jwt/login', body, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  }

  public async logout(): Promise<void> {
    try {
      await this.axiosInstance.post('/auth/jwt/logout');
    } catch (error) {
      console.error('Error logging out:', error);
      throw new Error('Error logging out');
    }
  }

  public async forgotPassword(email: string): Promise<void> {
    try {
      await this.axiosInstance.post('/auth/forgot-password', { email });
    } catch (error) {
      console.error('Error requesting password reset:', error);
      throw new Error('Error requesting password reset');
    }
  }

  public async resetPassword(token: string, password: string): Promise<void> {
    try {
      await this.axiosInstance.post('/auth/reset-password', { token, password });
    } catch (error) {
      console.error('Error resetting password:', error);
      throw new Error('Error resetting password');
    }
  }

  public async getCurrentUser(): Promise<CurrentUser> {
    try {
      const { data } = await this.axiosInstance.get('/auth/users/me');
      return data;
    } catch (error) {
      // 401 just means "not logged in" — expected for anonymous visitors on the
      // initial hydrate probe, so don't log it as an error.
      if (!(error instanceof AxiosError && error.response?.status === 401)) {
        console.error('Error getting current user:', error);
      }
      throw new Error('Error getting current user');
    }
  }

  // ── Profile (logged-in account) ────────────────────────────────────────────

  public async getMeetingHistory(): Promise<MeetingHistoryItem[]> {
    try {
      const { data } = await this.axiosInstance.get('/meeting/history');
      return data;
    } catch (error) {
      console.error('Error getting meeting history:', error);
      throw new Error('Error getting meeting history');
    }
  }

  public async updateProfile(payload: { name?: string; email?: string }): Promise<CurrentUser> {
    try {
      const { data } = await this.axiosInstance.patch('/auth/users/me', payload);
      return data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw new Error('Error updating profile');
    }
  }

  public async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await this.axiosInstance.post('/auth/users/me/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
    } catch (error) {
      console.error('Error changing password:', error);
      throw new Error('Error changing password');
    }
  }

  public async uploadAvatar(file: File): Promise<CurrentUser> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await this.axiosInstance.post('/auth/users/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return data;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      throw new Error('Error uploading avatar');
    }
  }

  public async deleteAccount(): Promise<void> {
    try {
      await this.axiosInstance.delete('/auth/users/me/account');
    } catch (error) {
      console.error('Error deleting account:', error);
      throw new Error('Error deleting account');
    }
  }

  public async resendVerification(email: string): Promise<void> {
    try {
      await this.axiosInstance.post('/auth/request-verify-token', { email });
    } catch (error) {
      console.error('Error requesting verification:', error);
      throw new Error('Error requesting verification');
    }
  }

  public async verifyEmail(token: string): Promise<void> {
    try {
      await this.axiosInstance.post('/auth/verify', { token });
    } catch (error) {
      console.error('Error verifying email:', error);
      throw new Error('Error verifying email');
    }
  }

  public async attachMeetingMaterial(meetingUid: string, file: File) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/attach-material`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return data;
    } catch (error) {
      console.error('Error attaching material:', error);
      throw new Error('Error attaching material');
    }
  }

  public async loginInMeeting({
    meetingUid,
    userUid,
    assistantInviteToken
  }: LoginInMeeting): Promise<LoginInMeetingResponse> {
    try {
      const response = await this.axiosInstance.post('/participants/login-in-meeting', {
        meeting_uid: meetingUid,
        user_uid: userUid,
        assistant_invite_token: assistantInviteToken
      });
      return response.data;
    } catch (error) {
      console.error('Error login in meeting:', error);
      throw new Error('Error login in meeting');
    }
  }

  public async generateToken({ meetingUid, identity, name }: RoomConnectParams): Promise<string> {
    try {
      const { data } = await this.axiosInstance.post('/rooms/room-token', {
        room_uid: meetingUid,
        identity,
        name
      });
      return data.token;
    } catch (error) {
      console.error('Error generating token:', error);
      throw new Error('Error generating token');
    }
  }

  public async createMeeting(params: CreateRoomParams) {
    try {
      const { data } = await this.axiosInstance.post('/meeting/', params);
      return data;
    } catch (error) {
      console.error('Error creating meeting:', error);
      throw new Error('Error creating meeting');
    }
  }

  public async getMeeting(meetingUid: string) {
    try {
      const { data } = await this.axiosInstance.get(`/meeting/${meetingUid}`);
      return data;
    } catch (error) {
      console.error('Error getting meeting:', error);
      throw new Error('Error getting meeting');
    }
  }

  public async getCurrentMeeting(meetingUid: string) {
    try {
      const { data } = await this.axiosInstance.get(`/meeting/${meetingUid}/current`);
      return data;
    } catch (error) {
      console.error('Error getting current meeting:', error);
      throw new Error('Error getting current meeting');
    }
  }

  public async getStudyMode(meetingUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.get(`/meeting/${meetingUid}/study-mode`);
      return data;
    } catch (error) {
      console.error('Error get study mode:', error);
      throw new Error('Error get study mode');
    }
  }

  public async changeStudyMode(meetingUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.put(`/meeting/${meetingUid}/change-study-mode`);
      return data;
    } catch (error) {
      console.error('Error switching study mode:', error);
      throw new Error('Error switching study mode');
    }
  }

  public async authorDisconnect(meetingUid: string) {
    try {
      const { data } = await this.axiosInstance.put(`/meeting/${meetingUid}/finish`);
      return data;
    } catch (error) {
      console.error('Error disconnect from meeting:', error);
      throw new Error('Error disconnect from meeting');
    }
  }

  public async checkRoom(meetingUid: string) {
    try {
      const { data } = await this.axiosInstance.get(`/meeting/${meetingUid}/check`);
      return data;
    } catch (error) {
      console.error('Error checking room:', error);
      throw new Error('Error checking room');
    }
  }

  public async uploadMeetingPdf(meetingUid: string, file: File): Promise<DocProps> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/upload-file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw new Error('Error uploading file');
    }
  }

  public async uploadMeetingDrawingPdf(meetingUid: string, file: File): Promise<DocProps> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/upload-drawing-file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return data;
    } catch (error) {
      console.error('Error uploading drawing file:', error);
      throw new Error('Error uploading drawing file');
    }
  }

  public async uploadBoard(meetingUid: string, userUid: string, image: Blob, pageIndex?: string) {
    try {
      const formData = new FormData();
      formData.append('meeting_uuid', meetingUid);
      formData.append('user_uuid', userUid);
      const filename = pageIndex
        ? `board_${userUid}_page_${pageIndex}.png`
        : `board_${userUid}.png`;
      formData.append('file', image, filename);

      await this.axiosInstance.post('/boards/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (error) {
      console.error('Error uploading board:', error);
      throw new Error('Error uploading board');
    }
  }

  public uploadBoardBeacon(
    meetingUid: string,
    userUid: string,
    image: Blob,
    pageIndex?: string
  ): boolean {
    const formData = new FormData();
    formData.append('meeting_uuid', meetingUid);
    formData.append('user_uuid', userUid);
    const filename = pageIndex ? `board_${userUid}_page_${pageIndex}.png` : `board_${userUid}.png`;
    formData.append('file', image, filename);

    return navigator.sendBeacon(`${this.axiosInstance.defaults.baseURL}/boards/`, formData);
  }

  public async getCurrentFileForMeeting(meetingUid: string): Promise<string> {
    try {
      const { data } = await this.axiosInstance.get(`/files/meeting/${meetingUid}/current-file`);

      return data;
    } catch (error) {
      console.error('Error getting the file:', error);
      throw new Error('Error getting the file');
    }
  }

  public async getActiveFileForMeeting(meetingUid: string): Promise<DocProps> {
    try {
      const { data } = await this.axiosInstance.get(`/files/meeting/${meetingUid}/active-file`);

      return data;
    } catch (error) {
      console.error('Error getting the active file:', error);
      throw new Error('Error getting the active file');
    }
  }

  public async getAllFiles(meetingUid: string): Promise<string[]> {
    try {
      const { data } = await this.axiosInstance.get(`/files/meeting/${meetingUid}/files`);

      return data;
    } catch (error) {
      console.error('Error getting files:', error);
      throw new Error('Error getting files');
    }
  }

  public async setActiveFile(meetingUid: string, fileId: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/active-file/${fileId}`
      );
      return data;
    } catch (error) {
      console.error('Error setting active file:', error);
      throw new Error('Error setting active file');
    }
  }

  public async setUnActiveFile(meetingUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(`/files/meeting/${meetingUid}/unactive-file`);
      return data;
    } catch (error) {
      console.error('Error setting unactive file:', error);
      throw new Error('Error setting unactive file');
    }
  }

  public async getUploadFiles(meetingUid: string): Promise<DocProps[]> {
    try {
      const { data } = await this.axiosInstance.get(`/files/meeting/${meetingUid}/upload-files`);
      return data;
    } catch (error) {
      console.error('Error getting upload files', error);
      throw new Error('Error getting upload files');
    }
  }

  public async getStudentFiles(meetingUid: string): Promise<DocProps[]> {
    try {
      const { data } = await this.axiosInstance.get(`/files/meeting/${meetingUid}/student-files`);
      return data;
    } catch (error) {
      console.error('Error getting student files', error);
      throw new Error('Error getting student files');
    }
  }

  public async shareFileWithStudentInMeeting(meetingUid: string, fileId: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/share-with-students/${fileId}`
      );
      return data;
    } catch {
      console.error('Error sharing file with students');
      throw new Error('Error sharing file with students');
    }
  }

  public async unshareFileWithStudentInMeeting(
    meetingUid: string,
    fileId: string
  ): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/files/meeting/${meetingUid}/unshare-with-students/${fileId}`
      );
      return data;
    } catch {
      console.error('Error unsharing file with students');
      throw new Error('Error unsharing file with students');
    }
  }

  public async deleteFileFromMeeting(meetingUid: string, fileId: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.delete(`/files/meeting/${meetingUid}/${fileId}`);
      return data;
    } catch (error) {
      console.error('Error deleting file from meeting', error);
      throw new Error('Error deleting file from meeting');
    }
  }

  public async getTeacherBoards(meetingUid: string): Promise<string[]> {
    try {
      const { data } = await this.axiosInstance.get(`/boards/teacher-boards/${meetingUid}`);

      return data;
    } catch (error) {
      console.error('Error getting teacher boards:', error);
      throw new Error('Error getting teacher boards');
    }
  }

  public async getZipWithBoards(meetingUid: string, userUid: string) {
    try {
      const data = await this.axiosInstance.get(`/boards/download-zip/${meetingUid}/${userUid}`, {
        responseType: 'blob'
      });

      if (data.status === 204) {
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error getting url for download zip with boards:', error);
      throw new Error('Error getting url for download zip with boards');
    }
  }

  public async addParticipant(meetingUid: string, userUid: string, userName: string) {
    try {
      const { data } = await this.axiosInstance.post(
        `/participants/meeting/${meetingUid}/add-participant`,
        { user_uuid: userUid, name: userName }
      );
      return data;
    } catch (error) {
      console.error('Error adding participant:', error);
      throw new Error('Error adding participant');
    }
  }

  public async disconnectParticipant(meetingUid: string, userUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.put(
        `/participants/meeting/${meetingUid}/disconnect-participant`,
        { user_uuid: userUid }
      );
      return data;
    } catch (error) {
      console.error('Error disconnecting participant:', error);
      throw new Error('Error disconnecting participant');
    }
  }

  public async getNameParticipant(meetingUid: string, userUid: string): Promise<string> {
    try {
      const { data } = await this.axiosInstance.post(
        `/participants/meeting/${meetingUid}/get-name-participant`,
        { user_uuid: userUid }
      );
      return data;
    } catch (error) {
      console.error('Error getting name of participant:', error);
      throw new Error('Error getting name of participant');
    }
  }

  public async getLinkTokenAssistant(meetingUid: string): Promise<string | null> {
    try {
      const { data } = await this.axiosInstance.get(
        `/meeting/${meetingUid}/get-invite-token-to-assistant`
      );
      return data;
    } catch (error) {
      console.error('Error getting token to invite assistant:', error);
      throw new Error('Error getting token to invite assistant');
    }
  }

  public async getMeetingByAssistant(assistantInviteToken: string): Promise<string | null> {
    try {
      const { data } = await this.axiosInstance.get(
        `/meeting/${assistantInviteToken}/get-meeting-by-assistant`
      );
      return data;
    } catch (error) {
      console.error('Error getting meeting by assistant:', error);
      throw new Error('Error getting meeting by assistant');
    }
  }

  public async createRoom(meetingUid: string, room: CreateEditMeetingRoom): Promise<MeetingRoom> {
    try {
      const { data } = await this.axiosInstance.post(`/rooms/${meetingUid}`, {
        name: room.name
      });
      return data;
    } catch (error) {
      console.error('Error creating room:', error);
      throw new Error('Error creating room');
    }
  }

  public async editRoom(
    meetingUid: string,
    roomId: number,
    room: CreateEditMeetingRoom
  ): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.put(`/rooms/${meetingUid}/${roomId}`, {
        name: room.name
      });
      return data;
    } catch (error) {
      console.error('Error editing room:', error);
      throw new Error('Error editing room');
    }
  }

  public async getRooms(meetingUid: string): Promise<MeetingRoom[]> {
    try {
      const { data } = await this.axiosInstance.get(`/rooms/${meetingUid}`);
      return data;
    } catch (error) {
      console.error('Error getting rooms:', error);
      throw new Error('Error getting rooms');
    }
  }

  public async uploadChatImage(meetingUuid: string, file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await this.axiosInstance.post(
        `/messages/upload-image?meeting_uuid=${meetingUuid}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return data;
    } catch (error) {
      console.error('Error uploading chat image', error);
      throw new Error('Error uploading chat image');
    }
  }

  public async sendMessage(message: SendMessage): Promise<boolean | undefined> {
    try {
      const { data } = await this.axiosInstance.post('/messages/send-message', message);
      return data;
    } catch (error) {
      console.error('Error sending message', error);
      throw new Error('Error sending message');
    }
  }

  public async getMessages(params: GetMessagesParams): Promise<GetMessages[]> {
    try {
      const { data } = await this.axiosInstance.get('/messages/', { params });
      return data;
    } catch (error) {
      console.error('Error getting messages', error);
      throw new Error('Error getting messages');
    }
  }

  public async updateSettings(meetingUid: string, settings: MeetingSettingsApi): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(`/meeting/${meetingUid}/settings`, settings);
      return data;
    } catch (error) {
      console.error('Error updating settings', error);
      throw new Error('Error updating settings');
    }
  }

  public async handUp(meetingUid: string, userUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/participants/meeting/${meetingUid}/hand-up`,
        {
          user_uuid: userUid
        }
      );
      return data;
    } catch (error) {
      console.error('Error of hand up', error);
      throw new Error('Error of hand up');
    }
  }

  public async handDown(meetingUid: string, userUid: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/participants/meeting/${meetingUid}/hand-down`,
        {
          user_uuid: userUid
        }
      );
      return data;
    } catch (error) {
      console.error('Error of hand down', error);
      throw new Error('Error of hand down');
    }
  }

  public async handUpInRoom(meetingUid: string, roomId: number): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/rooms/${meetingUid}/${roomId}/hand-up-in-room`
      );
      return data;
    } catch (error) {
      console.error('Error of hand up in room', error);
      throw new Error('Error of hand up in room');
    }
  }

  public async handDownInRoom(meetingUid: string, roomId: number): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(
        `/rooms/${meetingUid}/${roomId}/hand-down-in-room`
      );
      return data;
    } catch (error) {
      console.error('Error of hand down in room', error);
      throw new Error('Error of hand down in room');
    }
  }

  public async getHandUpOfRooms(meetingUid: string): Promise<string[]> {
    try {
      const { data } = await this.axiosInstance.get(`/rooms/${meetingUid}/hand-up`);
      return data;
    } catch (error) {
      console.error('Error getting hand up rooms', error);
      throw new Error('Error getting hand up rooms');
    }
  }

  public async getHandUpOfStudents(meetingUid: string): Promise<string[]> {
    try {
      const { data } = await this.axiosInstance.get(`/participants/${meetingUid}/hand-up`);
      return data;
    } catch (error) {
      console.error('Error getting hand up students', error);
      throw new Error('Error getting hand up students');
    }
  }

  public async editMeetingTitle(meetingUid: string, new_title: string): Promise<boolean> {
    try {
      const { data } = await this.axiosInstance.post(`/meeting/${meetingUid}/edit-title`, {
        new_title: new_title
      });
      return data;
    } catch (error) {
      console.error('Error editing title of meeting', error);
      throw new Error('Error editing title of meeting');
    }
  }
}

export default new Api();
